import dayjs from "@/libs/dayjs";
import FluentCalendar from "@/pages/taskpane/components/ui/Calendar";
import { Loader } from "@/pages/taskpane/components/ui/Loader";
import { generateCId } from "@/pages/taskpane/lib/utils/randomUUID";
import { useAppointmentStore, useAttendeesStore, useOutlookStore, useSettingStore, useThemeStore } from "@/pages/taskpane/shared/stores";
import React, { useEffect, useMemo, useState } from "react";
import { OnArgs } from "react-calendar/dist/shared/types";
import { useResourceCalendar } from "../hooks/useResourceCalendar";
import { EBusyStatus } from "../services/types/enum";
import { IGetResourceCalendarRequest, ResourceCalendarInfo } from "../services/types/resourceCalendarEntity";
import { ResourceEntity } from "../services/types/resourceEntity";
import { useFormatDateTime } from "@/pages/taskpane/shared/hooks";

type Props = {
  resource: ResourceEntity | null;
};

const ModalResourceCalendar = ({ resource }: Props) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [month, setMonth] = useState<string | null>(null);
  const { startDate } = useAppointmentStore();
  const { handleFormatDateWithSeparator, handleFormatTimeWithLanguage } = useFormatDateTime();
  const { timeZone } = useOutlookStore();
  const { isDarkMode } = useThemeStore();
  const { mainAttendee } = useAttendeesStore();
  const { timeFormat } = useSettingStore();
  const [highlightedDates, setHighlightedDates] = useState<Date[]>([]);
  const [resourceCalendarSchedule, setResourceCalendarSchedule] = useState<ResourceCalendarInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { getResourceCalendarBoldDateFromServer, getResourceCalendarScheduleFromServer } = useResourceCalendar();

  useEffect(() => {
    if (startDate) {
      setSelectedDate(startDate);
    }
  }, [startDate]);

  useEffect(() => {
    if (!selectedDate) {
      return;
    }
    getResourceCalendarSchedule();
    if (month !== dayjs(selectedDate).format("YYYY-MM")) {
      setMonth(dayjs(selectedDate).format("YYYY-MM"));
    }
  }, [selectedDate]);

  useEffect(() => {
    if (month) {
      getHighlightedDates();
    }
  }, [month]);

  const getHighlightedDates = async () => {
    if (!month) {
      return;
    }
    const startDate = dayjs(new Date(month)).startOf("month").format("YYYY-MM-DD");
    const endDate = dayjs(new Date(month)).endOf("month").format("YYYY-MM-DD");
    if (resource) {
      const request: IGetResourceCalendarRequest = {
        requestId: generateCId(),
        resourceId: resource.id,
        organizer: mainAttendee?.email || "",
        startDate: `${startDate}T00:00:00`,
        endDate: `${endDate}T23:59:59`,
        timeZoneStandardName: timeZone || "UTC",
      };
      setIsLoading(true);
      const boldDates = await getResourceCalendarBoldDateFromServer(request);
      setHighlightedDates(boldDates);
      setIsLoading(false);
    }
  };

  const getResourceCalendarSchedule = async () => {
    const startDate = dayjs(selectedDate).format("YYYY-MM-DD");
    if (resource) {
      const request: IGetResourceCalendarRequest = {
        requestId: generateCId(),
        resourceId: resource.id,
        organizer: mainAttendee?.email || "",
        startDate: `${startDate}T00:00:00`,
        endDate: `${startDate}T23:59:59`,
        timeZoneStandardName: timeZone || "UTC",
      };
      setIsLoading(true);
      const schedule = await getResourceCalendarScheduleFromServer(request);
      setIsLoading(false);
      setResourceCalendarSchedule(schedule);
    }
  };

  const handleClickDay = (date: Date) => {
    if(dayjs(date).isSame(dayjs(selectedDate), "day")) {
      return;
    }
    setSelectedDate(date);
  };

  const onActiveStartDateChange = (args: OnArgs) => {
    if(dayjs(args.activeStartDate).isSame(dayjs(selectedDate), "month")) {
      return;
    }
    setSelectedDate(args.activeStartDate || new Date());
  };

  const getBorderAfterStyle = (busyStatus: EBusyStatus): React.CSSProperties | undefined => {
    const primaryColor = isDarkMode ? "#479ef5" : "#0f6cbd";
    const oofColor = isDarkMode ? "rgba(245, 0, 216, 0.9)" : "rgba(180, 0, 158, 0.9)";
    const stripeWhiteColor = "#ffffff";

    const baseStyle: React.CSSProperties = {
      width: "6px",
      flexShrink: 0,
      border: `1px solid ${primaryColor}`,
    };

    switch (busyStatus) {
      case EBusyStatus.Busy:
        return {
          ...baseStyle,
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          backgroundColor: primaryColor,
        };
      case EBusyStatus.Free:
        return {
          ...baseStyle,
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          backgroundColor: "transparent",
        };
      case EBusyStatus.WorkingElsewhere:
        return {
          ...baseStyle,
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          backgroundColor: primaryColor,
          backgroundImage: `radial-gradient(circle, ${primaryColor} 0.15px, ${stripeWhiteColor} 0.7px), radial-gradient(circle, ${primaryColor} 0.15px, ${stripeWhiteColor} 0.7px), linear-gradient(${primaryColor}, ${primaryColor})`,
          backgroundPosition: "0 0, 2px 1px, 4px top",
          backgroundSize: "3px 3px, 2px 3px, 1px 100%",
          backgroundRepeat: "repeat-y",
        };
      case EBusyStatus.Tentative:
        return {
          ...baseStyle,
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          backgroundColor: primaryColor,
          backgroundImage: `repeating-linear-gradient(135deg, ${primaryColor} 0px, ${primaryColor} 3px, ${stripeWhiteColor} 3px, ${stripeWhiteColor} 6px), linear-gradient(${primaryColor}, ${primaryColor})`,
          backgroundPosition: "left, 4px top",
          backgroundSize: "4px 100%, 1px 100%",
          backgroundRepeat: "no-repeat",
        };
      case EBusyStatus.OutOfOffice:
        return {
          ...baseStyle,
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          borderColor: oofColor,
          backgroundColor: oofColor,
          backgroundImage: `linear-gradient(45deg, transparent 47%, white 47%, white 53%, transparent 53%), linear-gradient(-45deg, ${oofColor} 47%, white 47%, white 53%, ${oofColor} 53%), linear-gradient(${oofColor}, ${oofColor})`,
          backgroundPosition: "0 0, 0 0, 4px top",
          backgroundSize: "4px 6px, 4px 6px, 1px 100%",
          backgroundRepeat: "repeat-y, repeat-y, no-repeat",
        };
      default:
        return undefined;
    }
  };

  const sortedResourceCalendarSchedule = useMemo(() => {
    return resourceCalendarSchedule.sort((a, b) => {
      return dayjs(a.startDate).diff(dayjs(b.startDate));
    });
  }, [resourceCalendarSchedule]);

  return resource ? (
    <div className="flex flex-col gap-3 h-full px-5 py-4">
      <div className="shadow-elevationShadow4 dark:shadow-elevationShadow4Dark px-3 py-3 flex flex-col flex-1 min-h-0">
        <FluentCalendar
          value={selectedDate}
          onClickDay={handleClickDay}
          onActiveStartDateChange={onActiveStartDateChange}
          highlightedDates={highlightedDates}
          isDarkMode={isDarkMode}
        />
        <div className="border-t border-lightGray dark:border-darkMediumGray flex flex-col pt-3 flex-1 min-h-0 mt-3">
          <div className="flex flex-col gap-2 flex-1 min-h-0">
            <span className="text-xs font-semibold">
              {handleFormatDateWithSeparator(selectedDate || new Date())}
            </span>
            <div className="flex flex-col gap-2 overflow-y-auto no-scrollbar flex-1 min-h-0">
              {isLoading ? (
                <div className="flex justify-center items-center flex-1">
                 <Loader texts={["Loading..."]} />
                </div>
              ) : (
                sortedResourceCalendarSchedule.map((item: ResourceCalendarInfo) => {
                const afterStyle = getBorderAfterStyle(item.busyStatus);
                return (
                  <div key={item.id} className="flex gap-2">
                    <div className="flex flex-col" style={{ width: timeFormat.includes("A") ? "52px" : "32px" }}>
                      <span className="text-xs">{handleFormatTimeWithLanguage(item.startDate)}</span>
                      <span className="text-xs">{handleFormatTimeWithLanguage(item.endDate)}</span>
                    </div>
                    <div className="flex flex-col px-2 flex-1 rounded-r relative">
                      {afterStyle && <div style={afterStyle} className="pointer-events-none" />}
                      <span className="text-xs font-semibold ml-1">{item.subject}</span>
                      <span className="text-xs ml-1">{item.organizerEmail}</span>
                    </div>
                  </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : null;
};

export default ModalResourceCalendar;
